import { useQuery, keepPreviousData } from "@tanstack/react-query";

import { getSupplierListsPaged } from "../api/supplier-list-api";
import { SUPPLIER_LIST_QUERY_KEYS } from "../constants/query-keys";
import type { SupplierListsPagedParams } from "../types/supplier-list.types";

/**
 * React Query hook for the paged supplier-list screen (PRO-628). Re-fetches
 * whenever any param (tab/page/limit/search/sort/filters) changes — so a tab
 * click, page change, or sort issues exactly one scoped request. `keepPreviousData`
 * keeps the current page visible while the next loads, avoiding a full skeleton
 * flash on paging.
 */
export const useSupplierListsPaged = (params: SupplierListsPagedParams) => {
	return useQuery({
		queryKey: SUPPLIER_LIST_QUERY_KEYS.paged(
			params as unknown as Record<string, unknown>
		),
		queryFn: () => getSupplierListsPaged(params),
		placeholderData: keepPreviousData,
		// Lists are created/edited/archived/attached through separate flows that
		// never touch this query's cache. Treat the data as always-stale so it
		// revalidates on mount (back-navigation) AND on any key change — e.g.
		// switching to the "Panel Lists" tab after creating one must re-fetch,
		// not serve the tab's pre-creation cache. `keepPreviousData` still avoids
		// a skeleton flash while the fresh page loads.
		staleTime: 0,
		refetchOnMount: "always",
	});
};
